import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover'
import * as React from 'react'

import { useToggleState } from '~/hooks/use-toggle-state'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { Emoji, emojis } from './data'
import { Toolbar, ToolbarButton } from '@radix-ui/react-toolbar'
import { mergeRefs } from '~/lib/merge-refs'

// Emoji picker
// i don't know if use https://github.com/nolanlawson/emoji-picker-element?tab=readme-ov-file#css-variables
// or https://github.com/missive/emoji-mart <- Currently using this
export const EmojiPicker = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode
    onSelect: (emoji: Emoji, event: React.MouseEvent) => void
    state: ReturnType<typeof useToggleState>
    onOpenChange?: (v: boolean) => void
  } & Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>
>(({ children, onSelect, state, onOpenChange, ...props }, ref) => {
  const { isOn, handleOff, handleToggle } = state
  const nativeRef = React.useRef<HTMLButtonElement>(null)
  // eslint-disable-next-line react-compiler/react-compiler
  mergeRefs([nativeRef, ref])
  const iconsRef = React.useRef<HTMLDivElement>(null)
  const addEventListener = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleOff()
      }
    },
    [handleOff]
  )

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Tab') {
      // focus first button
      e.preventDefault()
      const buttons = iconsRef.current?.querySelectorAll('button')
      buttons?.[0]?.focus()
      return
    }
  }, [])

  const handleKeyDownEmoji = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        // focus trigger and close popover
        e.preventDefault()
        nativeRef.current?.focus()
        handleOff()
      }
    },
    [handleOff]
  )

  React.useEffect(() => {
    if (isOn) {
      document.addEventListener('keydown', addEventListener)
    } else {
      document.removeEventListener('keydown', addEventListener)
    }
    return () => {
      document.removeEventListener('keydown', addEventListener)
    }
  }, [isOn, addEventListener])

  return (
    <Popover
      open={isOn}
      onOpenChange={(v) => {
        onOpenChange && onOpenChange(v)
        handleToggle()
      }}
    >
      <PopoverTrigger ref={nativeRef} asChild onKeyDown={handleKeyDown}>
        {children}
      </PopoverTrigger>
      <PopoverPortal>
        {/* popover close background */}
        <PopoverContent
          collisionBoundary={document.getElementById('tab-content-wrapper')}
          sideOffset={4}
          side="bottom"
          align="center"
          onOpenAutoFocus={(e) => e.preventDefault()}
          asChild
          {...props}
        >
          <Toolbar
            className="z-[100] shadow-modal overflow-hidden flex space-x-2 bg-secondary p-2 rounded-lg bg-dark-base border border-dark-border-solid"
            ref={iconsRef}
          >
            {emojis.map((emoji, i) => {
              if (emoji.hideFromPicker) return null
              return (
                <SimpleTooltip
                  tabIndex={i}
                  key={emoji.name}
                  content={emoji.name}
                  side="top"
                  align="center"
                  delayDuration={0}
                >
                  <ToolbarButton asChild>
                    <IconButton
                      tabIndex={i}
                      size="sm"
                      className="h-9 w-9 text-lg"
                      onClick={(e) => {
                        onSelect(emoji, e)
                        handleOff()
                      }}
                      onKeyDown={handleKeyDownEmoji}
                    >
                      <span style={{ fontFamily: 'initial' }}>
                        {emoji.emoji}
                      </span>
                      <span className="sr-only">{emoji.name}</span>
                    </IconButton>
                  </ToolbarButton>
                </SimpleTooltip>
              )
            })}
          </Toolbar>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
})
